/**
 * Return Visitor Helper
 *
 * Fires a custom `return_visitor` analytics event when a user returns to the site
 * after a previous visit, enforcing a cooldown (default 30 minutes) between fires.
 *
 * Event parameters (pushed directly to the dataLayer / gtag):
 *  - visit_time_category: string (time since last visit: "0-1d", "1-7d", "7-30d", "30+d")
 */

interface ReturnVisitorOptions {
  cooldownMs?: number; // default 30min
  visitKey?: string; // localStorage key storing last visit timestamp
  firedKey?: string; // localStorage key storing last event fire timestamp
  dataLayerName?: string; // name of dataLayer
}

const DEFAULT_COOLDOWN_MS = 30 * 60 * 1000;
const DEFAULT_VISIT_KEY = 'site.lastVisitAt';
const DEFAULT_FIRED_KEY = 'site.lastReturnEventAt';

// Calculate time category based on milliseconds since last visit
function getVisitTimeCategory(millisecondsDiff: number): string {
  const dayInMs = 24 * 60 * 60 * 1000;

  if (millisecondsDiff < dayInMs) {
    return '0-1d';
  } else if (millisecondsDiff < 7 * dayInMs) {
    return '1-7d';
  } else if (millisecondsDiff < 30 * dayInMs) {
    return '7-30d';
  } else {
    return '30+d';
  }
}

// Parse and validate a timestamp from localStorage
function parseTimestamp(raw: string | null): number {
  if (raw === null) return NaN;
  const num = Number(raw);
  const result = Number.isFinite(num) && num > 0 ? num : NaN;
  // console.log('[returnVisitor] parseTimestamp:', { raw, result });
  return result;
}

// Check if enough time has passed since the given timestamp
function isCooldownExpired(timestamp: number, now: number, cooldownMs: number): boolean {
  const expired = !Number.isFinite(timestamp) || timestamp <= 0 || now - timestamp >= cooldownMs;
  // console.log('[returnVisitor] isCooldownExpired:', {
  //   timestamp,
  //   now,
  //   cooldownMs,
  //   timeDiff: now - timestamp,
  //   expired,
  // });
  return expired;
}

// Fire analytics event via dataLayer or gtag
function fireAnalyticsEvent(dataLayerName: string, visit_time_category: string | undefined): void {
  // console.log('[returnVisitor] fireAnalyticsEvent:', { dataLayerName, visit_time_category });
  const dl: any = (window as any)[dataLayerName];

  if (Array.isArray(dl) && typeof dl.push === 'function') {
    // console.log('[returnVisitor] Pushing to dataLayer');
    dl.push(
      {
        event: 'return_visitor',
        event_params: {
          visit_time_category,
        },
      },
      { event_params: undefined },
    );
  } else if (typeof (window as any).gtag === 'function') {
    // console.log('[returnVisitor] Calling gtag');
    (window as any).gtag('event', 'return_visitor', {
      visit_time_category,
    });
  }
}

export function returnVisitor(options: ReturnVisitorOptions = {}): boolean {
  // console.log('[returnVisitor] Function called with options:', options);
  if (typeof window === 'undefined') return false;

  const {
    cooldownMs = DEFAULT_COOLDOWN_MS,
    visitKey = DEFAULT_VISIT_KEY,
    firedKey = DEFAULT_FIRED_KEY,
    dataLayerName = process.env.GATSBY_DATA_LAYER_NAME || 'dataLayer',
  } = options;

  const now = Date.now();
  // console.log('[returnVisitor] Current timestamp:', now);

  try {
    const ls = window.localStorage;
    const previousVisitRaw = ls.getItem(visitKey);
    const hadVisited = previousVisitRaw !== null;

    if (!hadVisited) {
      // First ever visit (for this browser profile)
      // console.log('[returnVisitor] First visit detected');
      ls.setItem(visitKey, String(now));
      return false;
    }

    const prevVisitNum = parseTimestamp(previousVisitRaw);
    const lastFired = parseTimestamp(ls.getItem(firedKey));

    // Check cooldown against both last visit time and last fire time
    if (
      !isCooldownExpired(prevVisitNum, now, cooldownMs) ||
      !isCooldownExpired(lastFired, now, cooldownMs)
    ) {
      // Cooldown not expired - update visit timestamp but don't fire event
      // console.log('[returnVisitor] Cooldown not expired, skipping event');
      ls.setItem(visitKey, String(now));
      return false;
    }

    // Calculate time category based on time difference
    const visit_time_category =
      Number.isFinite(prevVisitNum) && prevVisitNum <= now
        ? getVisitTimeCategory(now - prevVisitNum)
        : undefined;
    // console.log('[returnVisitor] Visit time category:', visit_time_category);

    fireAnalyticsEvent(dataLayerName, visit_time_category);

    // Update both timestamps after successfully firing the event
    ls.setItem(visitKey, String(now));
    ls.setItem(firedKey, String(now));
    // console.log('[returnVisitor] Event fired successfully, timestamps updated');

    return true;
  } catch (error) {
    // Storage unavailable or other unexpected failure; fail silently.
    // console.error('[returnVisitor] Error:', error);
    return false;
  }
}

export default returnVisitor;
